import { useState, useEffect, useCallback } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { History, CheckCircle2, CopyX } from "lucide-react";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");

// A.4 — Revisar lançamentos antigos (possíveis duplicidades). Nada é apagado:
// "Duplicado" gera estorno com justificativa; "Correto" apenas marca.
export default function RevisaoLancamentosPage() {
  const [casos, setCasos] = useState([]);
  const carregar = useCallback(() => axios.get(`${API}/estoque-revisao/antigos`).then(r => setCasos(r.data)).catch(() => {}), []);
  useEffect(() => { carregar(); }, [carregar]);

  const marcar = async (lanc, status) => {
    let justificativa = null;
    if (status === "duplicado") {
      justificativa = window.prompt("Justificativa do estorno (obrigatória):");
      if (!justificativa || justificativa.trim().length < 5) { toast.error("Justificativa obrigatória"); return; }
    }
    try {
      const r = await axios.post(`${API}/estoque-revisao/${lanc.id}/marcar`, { status, justificativa });
      toast.success(r.data.message);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao marcar")); }
  };

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="revisao-lancamentos-page">
        <div>
          <h1 className="text-2xl font-bold text-luvcorp-navy flex items-center gap-2">
            <History className="h-6 w-6 text-luvcorp-gold" /> Revisar lançamentos antigos
          </h1>
          <p className="text-sm text-slate-500">Mesmo material saindo para a mesma obra no mesmo dia mais de uma vez. Marcar como <b>Duplicado</b> gera estorno com justificativa — o lançamento original não é apagado.</p>
        </div>
        {casos.length === 0 ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-400 text-sm">Nenhuma possível duplicidade encontrada nos lançamentos antigos.</CardContent></Card>
        ) : casos.map((c, ci) => (
          <Card key={ci} className="border-0 shadow-sm" data-testid={`caso-duplicidade-${ci}`}>
            <CardContent className="p-4">
              <p className="font-semibold text-luvcorp-navy text-sm mb-2">
                <Badge className="bg-amber-500 text-white mr-2">Possível duplicidade</Badge>
                {c.material} <span className="text-slate-400 text-xs">{c.codigo}</span> — {fmtD(c.dia)} · {c.lancamentos.length} lançamentos
              </p>
              <div className="space-y-1.5">
                {c.lancamentos.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 text-sm border-b border-slate-100 pb-1.5 flex-wrap">
                    <span className="text-slate-600">{l.obra || "—"} · qtd {l.quantidade} · {fmtBRL(l.valor_total)} <span className="text-xs text-slate-400">({l.motivo || "sem motivo"})</span></span>
                    {l.revisao?.status ? (
                      <Badge variant="outline" className={l.revisao.status === "duplicado" ? "bg-red-50 text-red-700 border-red-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}>
                        {l.revisao.status === "duplicado" ? "Duplicado (estornado)" : "Correto"} · {l.revisao.por}
                      </Badge>
                    ) : (
                      <span className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" onClick={() => marcar(l, "correto")} data-testid={`btn-correto-${l.id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Correto
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => marcar(l, "duplicado")} data-testid={`btn-duplicado-${l.id}`}>
                          <CopyX className="h-4 w-4 mr-1" /> Duplicado
                        </Button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </MainLayout>
  );
}
